import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { KpiCard, StatusPill } from "../components/UI";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Dry Run — rehearse month-end without touching real Xero or real emails.
 *
 * Door onto the engine shipped 1 Oct 2026. The engine itself (`_post_month_to_xero`
 * short-circuit on 9999-XX months, `_dry_run_post_month`, dashboard banner, fake
 * Resend) does the real work; this page is just the start/stop control + status.
 *
 * Modest by design: no "days since last rehearsal" nag, no source-month selector,
 * no fake Xero inbox view. If Todd (or Tiff, or whoever covers) needs to walk
 * through send/close, they start it here, then do the usual Month-end flow — the
 * reserved 9999-XX month lives alongside the real months on the Month-end page.
 */
export default function DryRun() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  const canOperate = user && (user.role === "Director" || user.role === "Office");

  const load = () => api.get("/dry-run/status").then(r => setStatus(r.data));
  useEffect(() => { load(); }, []);

  const start = async () => {
    if (!canOperate) return;
    setBusy(true); setFlash(null);
    try {
      const r = await api.post("/dry-run/start", {});
      setFlash({ tone: "green", text: r.data.message });
      await load();
    } catch (e) {
      setFlash({ tone: "red", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  const stop = async () => {
    if (!canOperate) return;
    if (!window.confirm("Stop the dry run and clear all rehearsal data? Fake invoices will be deleted; nothing in real Xero is affected.")) return;
    setBusy(true); setFlash(null);
    try {
      const r = await api.post("/dry-run/stop", {});
      setFlash({ tone: "green", text: `Dry run stopped. Removed ${r.data.deleted_runs} run and ${r.data.deleted_posts} fake invoice line(s).` });
      await load();
    } catch (e) {
      setFlash({ tone: "red", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  if (!status) return <div data-testid="dry-run-loading">Loading…</div>;

  const active = status.active;

  return (
    <div data-testid="dry-run-page">
      <PageHeader
        title="Dry run"
        subtitle="Rehearse the whole month-end flow — send, close, read the result — without touching real Xero or sending any real email."
      />

      {flash && (
        <div style={{
          padding: "10px 14px", marginBottom: 16, borderRadius: 8, fontSize: 13,
          background: flash.tone === "green" ? "#eafbea" : "#fde8e8",
          border: `1px solid ${flash.tone === "green" ? "var(--ach-green)" : "var(--ach-red)"}`,
          color: flash.tone === "green" ? "var(--ach-green)" : "var(--ach-red)",
        }} data-testid="dry-run-flash">
          {flash.text}
        </div>
      )}

      {active ? (
        <>
          <div style={{
            padding: "14px 18px", marginBottom: 20, borderRadius: 8,
            background: "repeating-linear-gradient(45deg, #FFEBB0, #FFEBB0 10px, #FFE08A 10px, #FFE08A 20px)",
            border: "2px solid var(--ach-amber, #c88c13)",
            color: "#5c3b00", fontSize: 14, fontWeight: 500,
          }} data-testid="dry-run-banner-page">
            🧪 <strong>DRY RUN ACTIVE on {status.month}</strong> — started by {status.started_by_name}.
            Nothing you do on month-end right now will touch Xero or email a customer. Invoice numbers come out as <code>INV-DRY-NNNN</code>.
          </div>

          <div className="ach-kpi-row" style={{gridTemplateColumns:"repeat(3, minmax(0,1fr))", marginBottom:24}}>
            <KpiCard tone="amber" number={status.month} label="Rehearsal month" testid="dry-run-kpi-month" />
            <KpiCard tone="neutral" number={status.runs[0]?.line_count ?? 0} label="Lines in the rehearsal" testid="dry-run-kpi-lines" />
            <KpiCard tone="neutral" number={status.fake_invoices_posted} label="Fake invoices posted so far" testid="dry-run-kpi-posted" />
          </div>

          <div className="ach-card" style={{marginBottom:20}}>
            <h3 className="ach-card__title">What to do next</h3>
            <ol style={{fontSize:13, color:"var(--ach-text-2)", lineHeight:1.7, marginTop:8}}>
              <li>Open <a href="#" onClick={(e)=>{e.preventDefault(); nav("/month-end")}} data-testid="link-month-end">Month-end billing</a>.</li>
              <li>Switch to month <strong>{status.month}</strong> in the month picker.</li>
              <li>Tick lines as reviewed, press <em>Send to Xero</em>. Invoice numbers come out as <code>INV-DRY-NNNN</code> and are stored locally only.</li>
              <li>Press <em>Close month</em> to see the entity-reference totals get written for this rehearsal month.</li>
              <li>When you're done, come back and press <em>Stop dry run</em>.</li>
            </ol>
          </div>

          {canOperate && (
            <button className="ach-btn ach-btn--danger" onClick={stop} disabled={busy} data-testid="dry-run-stop-btn" style={{fontSize:14}}>
              {busy ? "Stopping…" : "Stop dry run and clear rehearsal data"}
            </button>
          )}
        </>
      ) : (
        <>
          <div className="ach-card" style={{marginBottom:20}}>
            <h3 className="ach-card__title">Not currently running a dry run</h3>
            <p style={{fontSize:13, color:"var(--ach-text-2)", lineHeight:1.6, marginTop:8}}>
              Starting a dry run clones <strong>this month's live charge lines</strong> into a reserved rehearsal month (<code>9999-{String(new Date().getMonth()+1).padStart(2,"0")}</code>). From there you can tick lines, press Send, press Close — all the real controls — and nothing hits Xero or sends an email. Invoice numbers come out as <code>INV-DRY-NNNN</code>.
            </p>
            <p style={{fontSize:13, color:"var(--ach-text-2)", lineHeight:1.6}}>
              Safe to start and stop as many times as you like. Stopping deletes every rehearsal artefact.
            </p>
            {canOperate ? (
              <button className="ach-btn ach-btn--primary" onClick={start} disabled={busy} data-testid="dry-run-start-btn" style={{marginTop:12, fontSize:14}}>
                {busy ? "Starting…" : "Start a dry run →"}
              </button>
            ) : (
              <div style={{fontSize:12, color:"var(--ach-text-2)", marginTop:12, fontStyle:"italic"}}>
                Dry runs are started and stopped by a Director or Office user.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
